import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, PenLine, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useUserCredits } from '@/hooks/useUserCredits';
import { getAuthHeaders } from '@/lib/authHeaders';
import { useEventTracking } from '@/hooks/useEventTracking';
import { JacKeyIcon } from '@/components/wizard/JacKeyIcon';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const copy = {
  en: {
    trigger: 'Start this for me',
    title: 'Tell me what you want to say',
    subtitle: "Describe the feeling or situation — I'll write a first draft for you.",
    placeholder: "e.g. I want to thank my mom for always believing in me, even when I didn't believe in myself…",
    generate: 'Write my first draft',
    generating: 'Writing your draft…',
    cancel: 'Cancel',
    success: 'Draft ready! Edit it to make it yours.',
    failed: 'Could not generate. Try again.',
  },
  es: {
    trigger: 'Empieza por mí',
    title: 'Dime qué quieres decir',
    subtitle: 'Describe el sentimiento o la situación — escribiré un primer borrador para ti.',
    placeholder: 'ej. Quiero agradecer a mi mamá por siempre creer en mí, incluso cuando yo no creía en mí mismo/a…',
    generate: 'Escribe mi primer borrador',
    generating: 'Escribiendo tu borrador…',
    cancel: 'Cancelar',
    success: '¡Borrador listo! Edítalo para hacerlo tuyo.',
    failed: 'No se pudo generar. Inténtalo de nuevo.',
  },
  fr: {
    trigger: 'Commencer pour moi',
    title: 'Dites-moi ce que vous voulez dire',
    subtitle: "Décrivez le sentiment ou la situation — je vous écrirai un premier brouillon.",
    placeholder: "ex. Je veux remercier ma mère d'avoir toujours cru en moi…",
    generate: 'Écrire mon brouillon',
    generating: 'Écriture en cours…',
    cancel: 'Annuler',
    success: 'Brouillon prêt ! Modifiez-le pour le personnaliser.',
    failed: 'Échec de la génération. Réessayez.',
  },
  pt: {
    trigger: 'Comece para mim',
    title: 'Me diga o que você quer dizer',
    subtitle: 'Descreva o sentimento ou situação — escreverei um primeiro rascunho para você.',
    placeholder: 'ex. Quero agradecer minha mãe por sempre acreditar em mim…',
    generate: 'Escrever meu rascunho',
    generating: 'Escrevendo seu rascunho…',
    cancel: 'Cancelar',
    success: 'Rascunho pronto! Edite para deixar com a sua cara.',
    failed: 'Não foi possível gerar. Tente novamente.',
  },
  ar: {
    trigger: 'ابدأ بدلاً مني',
    title: 'أخبرني ماذا تريد أن تقول',
    subtitle: 'صف الشعور أو الموقف — سأكتب مسودة أولى لك.',
    placeholder: 'مثال: أريد أن أشكر أمي لأنها دائماً آمنت بي…',
    generate: 'اكتب مسودتي الأولى',
    generating: 'جارٍ الكتابة…',
    cancel: 'إلغاء',
    success: 'المسودة جاهزة! عدّلها لتصبح بأسلوبك.',
    failed: 'تعذر الإنشاء. حاول مرة أخرى.',
  },
  ja: {
    trigger: '代わりに書き始めて',
    title: '伝えたいことを教えてください',
    subtitle: '気持ちや状況を説明してください — 最初の下書きを書きます。',
    placeholder: '例：母にいつも信じてくれたことを感謝したい…',
    generate: '下書きを作成',
    generating: '下書きを作成中…',
    cancel: 'キャンセル',
    success: '下書きができました！自分らしく編集してください。',
    failed: '作成に失敗しました。もう一度お試しください。',
  },
  ko: {
    trigger: '대신 시작해 줘',
    title: '무엇을 전하고 싶은지 알려주세요',
    subtitle: '감정이나 상황을 설명해 주세요 — 첫 번째 초안을 작성해 드립니다.',
    placeholder: '예: 항상 나를 믿어준 엄마에게 감사하고 싶어…',
    generate: '초안 작성하기',
    generating: '초안 작성 중…',
    cancel: '취소',
    success: '초안이 준비되었습니다! 편집하여 나만의 것으로 만드세요.',
    failed: '작성에 실패했습니다. 다시 시도하세요.',
  },
  id: {
    trigger: 'Mulai untuk saya',
    title: 'Ceritakan apa yang ingin Anda sampaikan',
    subtitle: 'Jelaskan perasaan atau situasinya — saya akan menulis draf pertama untuk Anda.',
    placeholder: 'contoh: Saya ingin berterima kasih kepada ibu saya yang selalu percaya pada saya…',
    generate: 'Tulis draf pertama saya',
    generating: 'Menulis draf Anda…',
    cancel: 'Batal',
    success: 'Draf siap! Edit agar sesuai dengan gaya Anda.',
    failed: 'Gagal membuat. Coba lagi.',
  },

  hi: {
    trigger: 'मेरे लिए यह शुरू करें',
    title: 'मुझे बताएं कि आप क्या कहना चाहते हैं',
    subtitle: "Describe the feeling or situation — I'll write a first draft for you.",
    placeholder: "e.g. I want to thank my mom for always believing in me, even when I didn't believe in myself…",
    generate: 'मेरा पहला ड्राफ्ट लिखें',
    generating: 'आपका ड्राफ्ट लिखा जा रहा है…',
    cancel: 'रद्द करें',
    success: 'ड्राफ्ट तैयार है! इसे अपना बनाने के लिए संपादित करें।',
    failed: 'उत्पन्न नहीं हो सका। पुनः प्रयास करें।',
  },
};

// Relationship-aware placeholder examples
const RELATIONSHIP_PLACEHOLDERS: Record<string, Record<string, string>> = {
  crush: {
    en: "e.g. There's someone I can't stop thinking about. Every time they smile, I forget what I was saying…",
    es: "ej. Hay alguien en quien no puedo dejar de pensar. Cada vez que sonríe, olvido lo que iba a decir…",
    fr: "ex. Il y a quelqu'un à qui je ne peux pas arrêter de penser. Chaque fois qu'il/elle sourit…",
    pt: "ex. Tem alguém em quem não consigo parar de pensar. Toda vez que sorri, esqueço o que ia dizer…",
    ar: "مثال: هناك شخص لا أستطيع التوقف عن التفكير فيه. كل مرة يبتسم فيها…",
    ja: "例：どうしても気になる人がいます。その人が笑うたびに…",
    ko: "예: 계속 생각나는 사람이 있어요. 그 사람이 웃을 때마다…",
    id: "contoh: Ada seseorang yang tidak bisa saya lupakan. Setiap kali mereka tersenyum…",
  },
  parent: {
    en: "e.g. I want to thank my mom for always believing in me, even when I didn't believe in myself…",
    es: "ej. Quiero agradecer a mi mamá por siempre creer en mí, incluso cuando yo no creía en mí mismo/a…",
    fr: "ex. Je veux remercier ma mère d'avoir toujours cru en moi…",
    pt: "ex. Quero agradecer minha mãe por sempre acreditar em mim…",
    ar: "مثال: أريد أن أشكر أمي لأنها دائماً آمنت بي…",
    ja: "例：母にいつも信じてくれたことを感謝したい…",
    ko: "예: 항상 나를 믿어준 엄마에게 감사하고 싶어…",
    id: "contoh: Saya ingin berterima kasih kepada ibu saya yang selalu percaya pada saya…",
  },
  spouse: {
    en: "e.g. I want to remind my partner how much they mean to me and that I'd choose them all over again…",
    es: "ej. Quiero recordarle a mi pareja cuánto significa para mí y que la elegiría de nuevo…",
    fr: "ex. Je veux rappeler à mon/ma partenaire combien il/elle compte pour moi…",
    pt: "ex. Quero lembrar meu/minha parceiro/a o quanto significa para mim…",
    ar: "مثال: أريد أن أذكّر شريكي بمدى أهميته بالنسبة لي…",
    ja: "例：パートナーに、もう一度選ぶとしてもあなたを選ぶと伝えたい…",
    ko: "예: 파트너에게 얼마나 소중한지, 다시 선택해도 당신을 선택할 거라고…",
    id: "contoh: Saya ingin mengingatkan pasangan saya betapa berartinya mereka…",
  },
  friend: {
    en: "e.g. I want to tell my best friend that our friendship has been the most constant thing in my life…",
    es: "ej. Quiero decirle a mi mejor amigo/a que nuestra amistad ha sido lo más constante en mi vida…",
    fr: "ex. Je veux dire à mon meilleur ami que notre amitié est la chose la plus constante dans ma vie…",
    pt: "ex. Quero dizer ao meu melhor amigo que nossa amizade é a coisa mais constante na minha vida…",
    ar: "مثال: أريد أن أقول لصديقي المقرب أن صداقتنا هي الشيء الأكثر ثباتاً في حياتي…",
    ja: "例：親友に、私の人生で一番変わらないものは私たちの友情だと伝えたい…",
    ko: "예: 절친에게 우리 우정이 내 인생에서 가장 변함없는 것이라고…",
    id: "contoh: Saya ingin memberitahu sahabat saya bahwa persahabatan kami adalah hal paling konsisten dalam hidup saya…",
  },
  grandparent: {
    en: "e.g. I want to tell my grandma that her stories shaped who I am and I carry her wisdom everywhere…",
    es: "ej. Quiero decirle a mi abuela que sus historias formaron quién soy…",
    fr: "ex. Je veux dire à ma grand-mère que ses histoires m'ont façonné…",
    pt: "ex. Quero dizer à minha avó que suas histórias moldaram quem eu sou…",
    ar: "مثال: أريد أن أقول لجدتي أن قصصها شكّلت شخصيتي…",
    ja: "例：おばあちゃんに、あなたの話が私を形作ったと伝えたい…",
    ko: "예: 할머니에게 당신의 이야기가 저를 만들었다고 전하고 싶어…",
    id: "contoh: Saya ingin memberitahu nenek bahwa ceritanya membentuk siapa saya…",
  },
};

const getRelationshipPlaceholder = (relationship: string, lang: string, fallback: string): string => {
  const relPlaceholder = RELATIONSHIP_PLACEHOLDERS[relationship];
  if (relPlaceholder) return relPlaceholder[lang] || relPlaceholder.en || fallback;
  return fallback;
};

interface StartForMeProps {
  recipientName: string;
  relationship: string;
  template: string;
  onDraftReady: (draft: string) => void;
}

export function StartForMe({ recipientName, relationship, template, onDraftReady }: StartForMeProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const { useAiAssist } = useUserCredits();
  const { trackEvent } = useEventTracking();
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (!context.trim() || context.length < 10) return;
    setIsGenerating(true);
    trackEvent('start_for_me_requested', { template, relationship });

    try {
      await useAiAssist();
      const headers = await getAuthHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type: 'jackey_v2_generate',
          recipientName,
          relationship,
          template,
          mode: 'quick',
          lengthOption: 'standard',
          questions: ['What do you want to express?'],
          answers: [context],
          draftCount: 1,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) { toast.error(c.failed); return; }
        if (response.status === 402) { toast.info(c.failed); return; }
        throw new Error('Generate failed');
      }

      const data = await response.json();
      const draft = data.drafts?.[0] || data.message || '';
      if (draft) {
        onDraftReady(draft);
        toast.success(c.success);
        trackEvent('start_for_me_completed', { template, relationship });
        setIsOpen(false);
        setContext('');
      }
    } catch {
      toast.error(c.failed);
    } finally {
      setIsGenerating(false);
    }
  }, [context, recipientName, relationship, template, onDraftReady, c, trackEvent, useAiAssist]);

  if (!isOpen) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <PenLine className="h-4 w-4" />
        {c.trigger}
      </Button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-accent/30 rounded-lg p-4 border border-accent/50 space-y-3"
    >
      <div className="flex items-center gap-2 mb-1">
        <JacKeyIcon size="sm" />
        <div>
          <h4 className="text-sm font-medium text-foreground">{c.title}</h4>
          <p className="text-xs text-muted-foreground">{c.subtitle}</p>
        </div>
      </div>

      <Textarea
        placeholder={getRelationshipPlaceholder(relationship, language, c.placeholder)}
        value={context}
        onChange={(e) => setContext(e.target.value.slice(0, 500))}
        className="min-h-[100px] text-sm"
        disabled={isGenerating}
      />

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleGenerate}
          disabled={isGenerating || context.length < 10}
          className="flex-1 gap-2 bg-[hsl(160,35%,45%)] hover:bg-[hsl(160,35%,40%)] text-white"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {c.generating}
            </>
          ) : (
            <>
              <ArrowRight className="h-4 w-4" />
              {c.generate}
            </>
          )}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => { setIsOpen(false); setContext(''); }} disabled={isGenerating}>
          {c.cancel}
        </Button>
      </div>
    </motion.div>
  );
}
